/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { CalendarPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../../api';
import type { Auction } from '../../../api/areas/market';
import { Callout, DataTable, Field, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { GateList } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { useWrite, WriteError } from '../../../components/Write';
import { useAccess } from '../../../lib/access';
import { formatDateTime } from '../../../lib/format';
import { AuctionTimingButtons } from '../../operator/RegistryMarket';
import { selectClass } from '../../operator/shared';
import { RefreshButton } from '../../registry/shared';
import { AuctionLinks, useAuctionRows } from '../module';
import { AUCTION_STATUS, MarketBadge, money } from '../shared';

const DECIMAL = /^\d+(\.\d{1,4})?$/;
const CURRENCIES = ['VND', 'USD'];

function isoOf(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** "Tạo phiên đấu giá": a PUBLIC session under the rules in force (`POST /auctions`). */
function CreateAuctionForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('market');
  const [domain, setDomain] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [start, setStart] = useState('');
  const [reserve, setReserve] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const w = useWrite((_: null, opts) =>
    api.createAuction(
      { domain: domain.trim(), currency, start_price: start.trim(), reserve_price: reserve.trim() || null, starts_at: isoOf(startsAt), ends_at: isoOf(endsAt) ?? '' },
      opts,
    ),
  );
  const created = w.state.phase === 'done' ? w.state.result : undefined;
  const endIso = isoOf(endsAt);
  const startIso = isoOf(startsAt);
  const timesOk = Boolean(endIso) && (!startIso || (endIso ?? '') > startIso);
  const ok = domain.trim() && DECIMAL.test(start.trim()) && (!reserve.trim() || DECIMAL.test(reserve.trim())) && timesOk;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) void w.run(null).then((r) => r && onCreated());
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('backorders.domain')} className="sm:col-span-2">
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" autoComplete="off" required />
        </Field>
        <Field label={t('au.sessions.currency')}>
          <select className={selectClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('auction.startPrice')} hint={t('au.sessions.amountHint')}>
          <Input inputMode="decimal" value={start} onChange={(e) => setStart(e.target.value)} required />
        </Field>
        <Field label={t('au.sessions.reserve')} hint={t('au.sessions.reserveHint')}>
          <Input inputMode="decimal" value={reserve} onChange={(e) => setReserve(e.target.value)} />
        </Field>
        <Field label={t('auction.starts')} hint={t('au.sessions.startsHint')}>
          <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <Field label={t('auction.ends')}>
          <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
        </Field>
      </div>
      {!timesOk && endsAt ? <Callout tone="warn">{t('au.sessions.timesInvalid')}</Callout> : null}
      {created ? (
        <Callout tone="ok" title={t('au.sessions.created', { version: created.policy_version })}>
          <Link to={`/auctions/${encodeURIComponent(created.id)}`} className="font-medium text-primary hover:underline">
            {t('au.sessions.openCreated')}
          </Link>
        </Callout>
      ) : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <div>
        <Button type="submit" disabled={!ok || w.busy}>
          <CalendarPlus /> {t('au.sessions.create')}
        </Button>
      </div>
    </form>
  );
}

/** `/auctions?tab=sessions`, reference 66: create a session, and run the ones that need a hand. */
export function AuctionSessionsPage() {
  const { t } = useTranslation('market');
  const access = useAccess();
  const rows = useAuctionRows();
  const canManage = !access.client && access.has(['auction.manage']);
  const columns: Column<Auction>[] = [
    {
      key: 'domain',
      header: t('backorders.domain'),
      cell: (a) => (
        <Link to={`/auctions/${encodeURIComponent(a.id)}`} className="font-semibold text-primary hover:underline">
          {a.domain_name}
        </Link>
      ),
    },
    { key: 'status', header: t('auctions.status'), cell: (a) => <MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} /> },
    { key: 'price', header: t('auction.startPrice'), numeric: true, cell: (a) => money(a.start_price, a.currency) },
    { key: 'policy', header: t('au.detail.policy'), className: 'text-xs', cell: (a) => `v${a.policy_version}` },
    {
      key: 'when',
      header: t('au.sessions.window'),
      className: 'text-xs',
      cell: (a) => (
        <div className="space-y-0.5 whitespace-nowrap">
          <div>{formatDateTime(a.starts_at)}</div>
          <div className="text-muted-foreground">→ {formatDateTime(a.ends_at)}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('au.sessions.actions')}</span>,
      cell: (a) =>
        canManage ? (
          <span className="flex flex-wrap gap-2">
            <AuctionTimingButtons auction={a} onChanged={rows.reload} />
          </span>
        ) : null,
    },
  ];
  const pending = rows.status === 'success' ? rows.data.items.filter((a) => a.status === 'SCHEDULED' || a.status === 'OPEN' || a.status === 'CLOSED') : [];
  return (
    <>
      <PageHeader title={t('au.sessions.title')} subtitle={t('au.sessions.subtitle')} actions={<RefreshButton onClick={rows.reload} />} />
      <AuctionLinks here="sessions" />
      <div className="space-y-5">
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px] [&>*]:min-w-0">
          <Section title={t('au.sessions.createTitle')} description={t('au.sessions.createHint')}>
            {canManage ? <CreateAuctionForm onCreated={rows.reload} /> : <Callout tone="info">{t('au.sessions.noPermission')}</Callout>}
          </Section>
          <Section title={t('au.sessions.gatesTitle')}>
            <GateList
              gates={[
                { id: 'rules', title: t('au.sessions.gate.rules'), hint: t('au.sessions.gate.rulesHint'), ok: null },
                { id: 'live', title: t('au.sessions.gate.live'), hint: t('au.sessions.gate.liveHint'), ok: null },
                { id: 'override', title: t('au.sessions.gate.override'), hint: t('au.sessions.gate.overrideHint'), ok: null },
                { id: 'private', title: t('au.sessions.gate.private'), hint: t('au.sessions.gate.privateHint'), ok: null },
              ]}
            />
          </Section>
        </div>
        <Section flush title={t('au.sessions.pendingTitle')} description={t('au.sessions.pendingHint')}>
          {rows.status === 'loading' ? (
            <div className="px-5 pb-5">
              <Loading rows={3} />
            </div>
          ) : null}
          {rows.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={rows.error} onRetry={rows.reload} title={t('auctions.loadError')} />
            </div>
          ) : null}
          {rows.status === 'success' ? <DataTable columns={columns} rows={pending} rowKey={(a) => a.id} empty={<EmptyState title={t('au.sessions.nonePending')} />} /> : null}
        </Section>
      </div>
    </>
  );
}
